/**
 * Lógica pura de aderência da Cadeia de Ajuda (LST).
 * Sem dependências de React/Supabase — fácil de testar e reusar.
 */

import type { OrgMember } from '../../../hooks/useOrgMembers';
import {
  mapRoleTypeToCargo,
  type LstCargo,
  type LstDiaAderencia,
  type LstExecucao,
  type LstLideradoAderencia,
  type LstOcorrencia,
  type LstStatus,
  type LstTemplate,
} from '../types';
import { todayISO } from './lstService';
import { isTemplateDueOnDate } from './lstSchedule';
import { isLstSlotStillOpen, resolveDisplayLstStatus } from '../utils/lstEffectiveStatus';
import {
  resolveMemberLstCargo,
  templateAppliesToUser,
  type MemberRoleNameResolver,
} from '../utils/lstTemplateScope';

function keyOf(templateId: string, userId: string, date: string): string {
  return `${templateId}__${userId}__${date}`;
}

/**
 * Monta a matriz de aderência semanal de uma lista de liderados.
 *
 * Regras de farol (apenas rotinas críticas, dias passados/hoje):
 *  - vermelho: ao menos 1 rotina crítica perdida (não realizada).
 *  - laranja:  sem perdas, porém com desvios registrados.
 *  - verde:    todas as rotinas críticas do dia realizadas sem desvio.
 *  - neutro:   dia futuro ou cargo sem rotinas críticas.
 */
export function computeChainAdherence(params: {
  members: OrgMember[];
  templates: LstTemplate[];
  execucoes: LstExecucao[];
  weekDates: string[];
  resolveMemberRoleName?: MemberRoleNameResolver;
}): LstLideradoAderencia[] {
  const { members, templates, execucoes, weekDates, resolveMemberRoleName } = params;
  const today = todayISO();

  // Index execuções por (template, user, data)
  const execIndex = new Map<string, LstExecucao>();
  for (const e of execucoes) {
    execIndex.set(keyOf(e.template_id, e.user_id, e.data), e);
  }

  return members.map((member) => {
    const cargo = resolveMemberRoleName
      ? resolveMemberLstCargo(member, resolveMemberRoleName)
      : mapRoleTypeToCargo(member.roleType);
    const memberTemplates = templates.filter((t) =>
      templateAppliesToUser(t, cargo, member.id),
    );
    const criticas = memberTemplates.filter((t) => t.is_critical);

    const ocorrencias: LstOcorrencia[] = [];

    const dias: LstDiaAderencia[] = weekDates.map((date) => {
      let criticasRealizadas = 0;
      let desvios = 0;
      let perdidas = 0;
      let aguardando = 0;

      for (const tpl of memberTemplates) {
        if (!isTemplateDueOnDate(tpl, date, weekDates)) continue;

        const exec = execIndex.get(keyOf(tpl.id, member.id, date));
        const stillOpen = !exec && isLstSlotStillOpen(tpl.scheduled_time, date, today);
        const status = exec?.status ?? (stillOpen ? 'pendente' : 'nao_realizado');

        if (status === 'realizado_com_desvio') {
          ocorrencias.push({
            date,
            templateTitle: tpl.title,
            status: 'realizado_com_desvio',
            justificativa: exec?.justificativa_desvio ?? null,
            isCritical: tpl.is_critical,
          });
        } else if (status === 'nao_realizado' && (exec || tpl.is_critical)) {
          // Perda registrada explicitamente OU rotina crítica não preenchida em dia passado/hoje.
          ocorrencias.push({
            date,
            templateTitle: tpl.title,
            status: 'nao_realizado',
            justificativa: exec?.justificativa_desvio ?? null,
            isCritical: tpl.is_critical,
          });
        }

        if (tpl.is_critical && stillOpen) {
          aguardando += 1;
        } else if (tpl.is_critical) {
          if (status === 'realizado') criticasRealizadas += 1;
          else if (status === 'realizado_com_desvio') desvios += 1;
          else perdidas += 1;
        }
      }

      const criticasTotal = criticas.length;
      let nivel: LstDiaAderencia['nivel'];
      const dayStillAhead = date > today;
      if (criticasTotal === 0 || dayStillAhead || (perdidas === 0 && desvios === 0 && aguardando > 0)) nivel = 'neutro';
      else if (perdidas > 0) nivel = 'vermelho';
      else if (desvios > 0) nivel = 'laranja';
      else nivel = 'verde';

      return { date, nivel, criticasTotal, criticasRealizadas, desvios, perdidas };
    });

    // Pauta ordenada por data
    ocorrencias.sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));

    return { member, cargo, dias, ocorrencias };
  });
}

/** Conta liderados com pelo menos um dia em vermelho na semana (gatilho de Andon). */
export function countAndonAlerts(rows: LstLideradoAderencia[]): number {
  return rows.filter((r) => r.dias.some((d) => d.nivel === 'vermelho')).length;
}

// ─── Raio-X do Dia (drill-down) ─────────────────────────────

/** Detalhe de uma rotina dentro do "Raio-X do Dia". */
export interface LstDiaRotinaDetalhe {
  template: LstTemplate;
  status: LstStatus;
  justificativa: string | null;
  actionId: string | null;
}

/** Resumo consolidado do dia auditado. */
export interface LstDiaRaioX {
  date: string;
  cargo: LstCargo;
  detalhes: LstDiaRotinaDetalhe[];
  total: number;
  realizadas: number;
  desvios: number;
  perdidas: number;
  criticasTotal: number;
  criticasOk: number;
  pct: number;
}

/** Minutos no dia para ordenação cronológica rígida (mesma regra do checklist). */
function templateSortKey(t: LstTemplate): number {
  const time = t.scheduled_time;
  if (time && /^\d{1,2}:\d{2}$/.test(time)) {
    const [h, m] = time.split(':').map(Number);
    return h * 60 + m;
  }
  const base = { inicio: 0, meio: 12 * 60, fim: 18 * 60 }[t.period];
  return base + t.sort_order;
}

/**
 * Monta o "Raio-X do Dia" de um usuário: a lista completa das rotinas do seu
 * cargo naquela data, cruzada com a execução (status + justificativa de desvio).
 */
export function buildDiaRaioX(params: {
  cargo: LstCargo;
  templates: LstTemplate[];
  execucoes: LstExecucao[];
  userId: string;
  date: string;
}): LstDiaRaioX {
  const { cargo, templates, execucoes, userId, date } = params;
  const today = todayISO();

  const execByTemplate = new Map<string, LstExecucao>();
  for (const e of execucoes) {
    if (e.user_id === userId && e.data === date) execByTemplate.set(e.template_id, e);
  }

  const monthDates = (() => {
    const [y, m] = date.split('-').map(Number);
    const total = new Date(y, m, 0).getDate();
    return Array.from({ length: total }, (_, i) => {
      const d = i + 1;
      return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    });
  })();

  const dueTemplates = templates
    .filter(
      (t) =>
        templateAppliesToUser(t, cargo, userId) && isTemplateDueOnDate(t, date, monthDates),
    )
    .sort((a, b) => templateSortKey(a) - templateSortKey(b));

  const detalhes: LstDiaRotinaDetalhe[] = dueTemplates.map((template) => {
    const exec = execByTemplate.get(template.id) ?? null;
    const display = resolveDisplayLstStatus(template, exec, date, today);
    return {
      template,
      status: display === 'pendente' ? 'nao_realizado' : display,
      justificativa: exec?.justificativa_desvio ?? null,
      actionId: exec?.action_id ?? null,
    };
  });

  let realizadas = 0;
  let desvios = 0;
  let perdidas = 0;
  let criticasTotal = 0;
  let criticasOk = 0;
  for (const template of dueTemplates) {
    const exec = execByTemplate.get(template.id) ?? null;
    const display = resolveDisplayLstStatus(template, exec, date, today);
    if (display === 'pendente') continue;
    if (display === 'realizado') realizadas += 1;
    else if (display === 'realizado_com_desvio') desvios += 1;
    else perdidas += 1;
    if (template.is_critical) {
      criticasTotal += 1;
      if (display === 'realizado') criticasOk += 1;
    }
  }

  const total = detalhes.length;
  const pct = total === 0 ? 0 : Math.round(((realizadas + desvios) / total) * 100);

  return { date, cargo, detalhes, total, realizadas, desvios, perdidas, criticasTotal, criticasOk, pct };
}
